((globalScope) => {
  'use strict';

  const QUIZ_MODAL_ID = 'kex-business-quiz';
  const CACHE_VERSION = 'v2';
  const CACHE_TTL = 5 * 60 * 1000;
  const PAGE_SIZE = 250;
  const MAX_PAGES = 10;
  const REQUEST_TIMEOUT = 12000;

  const TYPE_FAMILY_MAP = {
    'bases para bebidas': 'powder',
    'base para bebidas': 'powder',
    harinas: 'powder',
    jarabes: 'syrup',
    jarabe: 'syrup',
    salsas: 'sauce',
    salsa: 'sauce',
    'tes y tisanas': 'tea',
    'te y tisanas': 'tea',
    concentrados: 'concentrate',
    concentrado: 'concentrate',
    'jellys y popping pearls': 'bubble',
    'jellies y popping pearls': 'bubble',
    tapioca: 'bubble',
    desechables: 'disposable',
    desechable: 'disposable',
    cafe: 'coffee',
    pumps: 'accessory',
    'equipo para cafeteria': 'equipment',
    'dispensadores de bebidas': 'equipment',
    'sampler box': 'sampler'
  };

  const FAMILY_PHRASES = {
    syrup: ['jarabe', 'syrup'],
    powder: ['base para bebida', 'polvo para bebida', 'frappe', 'frappé'],
    sauce: ['salsa', 'sauce', 'topping'],
    tea: ['tisana', 'té verde', 'te verde', 'té negro', 'te negro', 'tea'],
    concentrate: ['concentrado', 'concentrate', 'puré de fruta', 'pure de fruta', 'puree'],
    bubble: ['bubble', 'boba', 'popping', 'tapioca', 'perlas', 'pearls', 'jelly'],
    disposable: ['vaso', 'tapa', 'popote', 'desechable', 'cup', 'lid', 'straw'],
    coffee: ['café en grano', 'cafe en grano', 'coffee beans', 'espresso'],
    accessory: ['bomba dosificadora', 'dosificador', 'pump'],
    equipment: ['equipo para cafetería', 'equipo para cafeteria', 'dispensador']
  };

  const TRAIT_PHRASES = {
    fruit: ['mango', 'fresa', 'strawberry', 'lychee', 'cereza', 'cherry', 'pera', 'pear', 'durazno', 'peach', 'blueberry', 'arándano', 'arandano', 'raspberry', 'frambuesa', 'maracuyá', 'maracuya', 'piña', 'pina', 'coco', 'fruit', 'fruta'],
    mixology: ['mixología', 'mixologia', 'mixology', 'cocktail', 'cóctel', 'coctel', 'mocktail', 'margarita', 'spritz', 'sour'],
    sugar_free: ['sugar free', 'sin azúcar', 'sin azucar'],
    premium: ['premium', 'gourmet', 'signature']
  };

  const PREMIUM_VENDORS = new Set(['davinci', 'big train', 'tea zone', 'oregon chai']);

  const QUESTION_RULES = [
    {
      id: 'businessType',
      answers: [
        { id: 'cafeteria', groups: { syrup: 15, powder: 12, sauce: 10, tea: 8, disposable: 6, coffee: 8 } },
        { id: 'bar', groups: { mixology: 16, fruit: 14, syrup: 11, concentrate: 9, sauce: 6 } },
        { id: 'restaurante', groups: { sauce: 11, tea: 10, concentrate: 10, disposable: 8, syrup: 7, equipment: 5 } },
        { id: 'reposteria', groups: { sauce: 16, powder: 10, syrup: 9, fruit: 7 } },
        { id: 'bubble-tea', groups: { bubble: 18, tea: 13, fruit: 10, disposable: 6 } }
      ]
    },
    {
      id: 'menuFocus',
      answers: [
        { id: 'hot-drinks', groups: { coffee: 12, syrup: 10, sauce: 10, tea: 8, powder: 6 } },
        { id: 'cold-drinks', groups: { powder: 14, syrup: 12, fruit: 10, bubble: 7, tea: 6, concentrate: 6 } },
        { id: 'cocktails', groups: { mixology: 18, fruit: 13, syrup: 9, concentrate: 9 } },
        { id: 'desserts', groups: { sauce: 15, powder: 10, syrup: 8, fruit: 6 } },
        { id: 'versatile', groups: { syrup: 10, sauce: 9, powder: 9, tea: 8, disposable: 5 } }
      ]
    },
    {
      id: 'productNeed',
      answers: [
        { id: 'syrups', groups: { syrup: 20 } },
        { id: 'powders', groups: { powder: 20 } },
        { id: 'bubble', groups: { bubble: 20 } },
        { id: 'teas', groups: { tea: 18, concentrate: 12 } },
        { id: 'disposable', groups: { disposable: 20, accessory: 12, equipment: 10 } },
        { id: 'sauces', groups: { sauce: 20 } }
      ]
    },
    {
      id: 'priority',
      answers: [
        { id: 'margin', groups: { syrup: 10, sauce: 10, powder: 8, disposable: 4 }, pricePreference: 'balanced' },
        { id: 'speed', groups: { disposable: 12, syrup: 10, sauce: 8, tea: 7, concentrate: 6, accessory: 6 }, pricePreference: 'accessible' },
        { id: 'premium', groups: { premium: 12, powder: 10, fruit: 8, syrup: 8 }, pricePreference: 'premium' },
        { id: 'light', groups: { sugar_free: 18, tea: 8, fruit: 6 } },
        { id: 'starter', groups: { syrup: 8, powder: 8, tea: 7, sauce: 7 }, pricePreference: 'accessible' }
      ]
    }
  ];

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function containsPhrase(value, phrase) {
    const normalizedValue = normalizeText(value);
    const normalizedPhrase = normalizeText(phrase);
    if (!normalizedValue || !normalizedPhrase) return false;
    return ` ${normalizedValue} `.includes(` ${normalizedPhrase} `);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function toCents(value) {
    const parsed = Number.parseFloat(String(value == null ? '' : value));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
  }

  function ensureTrailingSlash(value) {
    const route = String(value || '/');
    return route.endsWith('/') ? route : `${route}/`;
  }

  function getAvailableVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.find((variant) => Boolean(variant.available)) || null;
  }

  function getProductImage(product, variant) {
    const variantImage = variant?.featured_image;
    if (typeof variantImage === 'string') return variantImage;
    if (variantImage?.src) return variantImage.src;
    const images = Array.isArray(product?.images) ? product.images : [];
    const image = images[0];
    if (typeof image === 'string') return image;
    return image?.src || '';
  }

  function detectFamily(product) {
    const normalizedType = normalizeText(product.type);
    if (TYPE_FAMILY_MAP[normalizedType]) return TYPE_FAMILY_MAP[normalizedType];

    const source = [product.title, product.handle, ...product.tags].filter(Boolean).join(' ');
    const family = Object.entries(FAMILY_PHRASES).find(([, phrases]) => (
      phrases.some((phrase) => containsPhrase(source, phrase))
    ));
    return family ? family[0] : 'other';
  }

  function detectSignals(product, family) {
    const signals = new Set(family && family !== 'other' ? [family] : []);
    const source = [product.title, product.handle, ...product.tags].filter(Boolean).join(' ');

    Object.entries(TRAIT_PHRASES).forEach(([trait, phrases]) => {
      if (phrases.some((phrase) => containsPhrase(source, phrase))) signals.add(trait);
    });

    if (product.tags.some((tag) => normalizeText(tag) === 'sf')) signals.add('sugar_free');
    if (PREMIUM_VENDORS.has(normalizeText(product.vendor))) signals.add('premium');
    return [...signals];
  }

  function normalizeCatalogProduct(product, routes = {}) {
    if (!product || !product.id || !product.handle || !product.title) return null;
    const variant = getAvailableVariant(product);
    const root = ensureTrailingSlash(routes.root || '/');
    const tags = Array.isArray(product.tags) ? product.tags.map(String) : [];
    const normalized = {
      id: Number(product.id),
      title: String(product.title),
      handle: String(product.handle),
      url: `${root}products/${encodeURIComponent(product.handle)}`,
      vendor: String(product.vendor || ''),
      type: String(product.product_type || product.type || ''),
      tags,
      available: Boolean(variant),
      price: variant ? toCents(variant.price) : 0,
      compare_at_price: variant ? toCents(variant.compare_at_price) : 0,
      variant_id: variant ? Number(variant.id) : 0,
      subtitle: variant && variant.title && variant.title !== 'Default Title' ? String(variant.title) : '',
      image: getProductImage(product, variant)
    };
    const family = detectFamily(normalized);
    return {
      ...normalized,
      family,
      signals: detectSignals(normalized, family)
    };
  }

  function restoreCachedProduct(product) {
    if (!product || !product.id || !product.handle) return null;
    const normalized = {
      ...product,
      id: Number(product.id),
      variant_id: Number(product.variant_id || 0),
      price: Number(product.price || 0),
      compare_at_price: Number(product.compare_at_price || 0),
      tags: Array.isArray(product.tags) ? product.tags.map(String) : []
    };
    const family = normalized.family || detectFamily(normalized);
    return {
      ...normalized,
      family,
      signals: Array.isArray(normalized.signals) ? [...new Set(normalized.signals)] : detectSignals(normalized, family)
    };
  }

  function resolveAnswerRule(questionId, answerId) {
    const question = QUESTION_RULES.find((item) => item.id === questionId);
    if (!question) return null;
    const answer = question.answers.find((item) => item.id === answerId);
    return answer ? { question, answer } : null;
  }

  function getPriceBias(product, preference) {
    if (preference === 'accessible') {
      if (product.price <= 32000) return 8;
      if (product.price <= 50000) return 3;
      return -5;
    }
    if (preference === 'premium') {
      if (product.price >= 65000) return 8;
      if (product.price >= 45000) return 3;
      return -5;
    }
    if (preference === 'balanced') {
      return product.price >= 25000 && product.price <= 80000 ? 5 : 0;
    }
    return 0;
  }

  function buildProfile(answers) {
    const selected = QUESTION_RULES.map((question) => {
      const answerId = answers?.[question.id];
      const answer = question.answers.find((item) => item.id === answerId);
      return answer ? { questionId: question.id, answer } : null;
    }).filter(Boolean);
    const productNeed = selected.find((item) => item.questionId === 'productNeed');
    return {
      selected,
      primaryGroups: productNeed ? Object.keys(productNeed.answer.groups) : []
    };
  }

  function scoreSingleProduct(product, profile, context) {
    if (!product?.available || !product.variant_id) return null;
    if (context.currentProductId && Number(product.id) === Number(context.currentProductId)) return null;

    const signals = new Set(product.signals || []);
    const matches = [];
    let score = 0;
    let pricePreference = null;

    profile.selected.forEach(({ questionId, answer }) => {
      const matchedGroups = Object.entries(answer.groups || {})
        .filter(([group]) => signals.has(group));

      if (answer.pricePreference) pricePreference = answer.pricePreference;

      if (matchedGroups.length) {
        const weights = matchedGroups.map(([, weight]) => Number(weight || 0));
        score += Math.max(...weights) + Math.max(0, weights.length - 1) * 3;
        if (questionId === 'productNeed') score += 90;
        if (questionId === 'menuFocus') score += 24;
        if (questionId === 'businessType') score += 16;
        if (questionId === 'priority') score += 10;
        matches.push({ questionId, answerId: answer.id, groups: matchedGroups.map(([group]) => group) });
      } else if (questionId === 'productNeed') {
        score -= 140;
      } else if (questionId === 'menuFocus') {
        score -= 18;
      } else if (questionId === 'businessType') {
        score -= 8;
      } else if (questionId === 'priority') {
        score -= 4;
      }
    });

    score += getPriceBias(product, pricePreference);
    if (product.compare_at_price > product.price) score += 2;
    const primaryMatchCount = profile.primaryGroups.filter((group) => signals.has(group)).length;

    return {
      product,
      score,
      matches,
      primaryMatchCount,
      family: product.family || 'other'
    };
  }

  function diversifyRecommendations(scored, profile, limit) {
    const selected = [];
    const selectedIds = new Set();
    const familyCounts = new Map();
    const primaryFamilies = new Set(profile.primaryGroups);

    const add = (recommendation, enforceFamilyLimit = true) => {
      if (!recommendation || selectedIds.has(recommendation.product.id)) return false;
      const family = recommendation.family || 'other';
      const familyLimit = primaryFamilies.has(family) ? 3 : 2;
      if (enforceFamilyLimit && (familyCounts.get(family) || 0) >= familyLimit) return false;
      selected.push(recommendation);
      selectedIds.add(recommendation.product.id);
      familyCounts.set(family, (familyCounts.get(family) || 0) + 1);
      return true;
    };

    profile.primaryGroups.forEach((group) => {
      if (selected.length >= Math.min(3, limit)) return;
      add(scored.find((item) => item.product.signals.includes(group)));
    });

    scored.forEach((recommendation) => {
      if (selected.length >= Math.min(3, limit)) return;
      if (recommendation.primaryMatchCount > 0) add(recommendation);
    });

    scored.forEach((recommendation) => {
      if (selected.length >= limit) return;
      add(recommendation);
    });

    scored.forEach((recommendation) => {
      if (selected.length >= limit) return;
      add(recommendation, false);
    });

    return selected;
  }

  function scoreProducts(products, answers, context = {}, limit = 6) {
    const profile = buildProfile(answers);
    const scored = (Array.isArray(products) ? products : [])
      .map((product) => scoreSingleProduct(product, profile, context))
      .filter(Boolean)
      .sort((a, b) => (
        b.score - a.score
        || b.primaryMatchCount - a.primaryMatchCount
        || a.product.price - b.product.price
        || a.product.id - b.product.id
      ));
    return diversifyRecommendations(scored, profile, limit);
  }

  const ENGINE = Object.freeze({
    QUESTION_RULES,
    normalizeText,
    containsPhrase,
    normalizeCatalogProduct,
    restoreCachedProduct,
    resolveAnswerRule,
    buildProfile,
    scoreProducts
  });

  if (typeof module !== 'undefined' && module.exports) module.exports = ENGINE;
  if (globalScope) globalScope.KexBusinessQuizEngine = ENGINE;
  if (typeof document === 'undefined') return;

  class KexBusinessQuiz {
    constructor(modal) {
      this.modal = modal;
      this.root = modal.querySelector('.js-business-quiz');
      if (!this.root) return;

      this.config = this.readConfig();
      if (!this.config || !Array.isArray(this.config.questions)) return;

      this.questions = this.config.questions;
      this.strings = this.config.strings || {};
      this.views = Object.fromEntries(
        [...this.root.querySelectorAll('[data-view]')].map((view) => [view.dataset.view, view])
      );
      this.stepCopy = this.root.querySelector('[data-quiz-step-copy]');
      this.progressBar = this.root.querySelector('[data-quiz-progressbar]');
      this.progressFill = this.root.querySelector('[data-quiz-progress]');
      this.questionTitle = this.root.querySelector('[data-quiz-question-title]');
      this.questionCopy = this.root.querySelector('[data-quiz-question-copy]');
      this.answersContainer = this.root.querySelector('[data-quiz-answers]');
      this.loadingMessage = this.root.querySelector('[data-quiz-loading-message]');
      this.summary = this.root.querySelector('[data-quiz-summary]');
      this.resultsTitle = this.root.querySelector('[data-quiz-results-title]');
      this.resultsGrid = this.root.querySelector('[data-quiz-results]');
      this.notice = this.root.querySelector('[data-quiz-notice]');
      this.selectionCopy = this.root.querySelector('[data-quiz-selection-copy]');
      this.answers = {};
      this.stepIndex = 0;
      this.catalog = null;
      this.catalogPromise = null;
      this.recommendations = [];
      this.context = {};
      this.isSubmitting = false;
      this.isFinishing = false;

      this.bindEvents();
      this.reset();
      this.showView('intro');
    }

    readConfig() {
      const script = this.root.querySelector('[data-kex-business-quiz-config]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    bindEvents() {
      document.addEventListener('click', this.handleDocumentClick.bind(this));
      this.root.addEventListener('click', this.handleRootClick.bind(this));
      this.root.addEventListener('change', this.handleRootChange.bind(this));
    }

    copy(key, fallback = '') {
      return String(this.strings[key] || fallback);
    }

    interpolate(template, replacements) {
      return Object.entries(replacements).reduce(
        (value, [key, replacement]) => value.replaceAll(`__${key}__`, String(replacement)),
        String(template || '')
      );
    }

    handleDocumentClick(event) {
      const trigger = event.target.closest('[data-open-business-quiz]');
      if (!trigger) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      this.context = {
        currentProductId: Number(trigger.dataset.businessQuizCurrentProductId || 0)
      };
      this.reset();
      this.showView('intro');
      this.modal.open(trigger);
      this.focusView(this.views.intro?.querySelector('h2'));
      this.loadCatalog().catch(() => null);
    }

    handleRootClick(event) {
      const actionButton = event.target.closest('[data-quiz-action]');
      if (actionButton) {
        event.preventDefault();
        this.handleAction(actionButton.dataset.quizAction);
        return;
      }

      const answerButton = event.target.closest('[data-quiz-answer]');
      if (answerButton) {
        event.preventDefault();
        this.handleAnswer(answerButton.dataset.quizAnswer);
        return;
      }

      const singleAddButton = event.target.closest('[data-quiz-single-add]');
      if (singleAddButton) {
        event.preventDefault();
        const variantId = Number(singleAddButton.dataset.quizSingleAdd || 0);
        if (variantId) this.addItems([{ id: variantId, quantity: 1 }], { checkout: false });
      }
    }

    handleRootChange(event) {
      if (!event.target.matches('[data-quiz-select]')) return;
      this.syncSelectedCards();
      this.updateSelectionCopy();
    }

    handleAction(action) {
      if (this.isFinishing || this.isSubmitting) return;
      if (action === 'start') {
        this.stepIndex = 0;
        this.renderQuestion();
      } else if (action === 'back') {
        if (this.stepIndex > 0) {
          this.stepIndex -= 1;
          this.renderQuestion();
        } else {
          this.showView('intro');
          this.focusView(this.views.intro?.querySelector('h2'));
        }
      } else if (action === 'restart') {
        this.reset();
        this.showView('intro');
        this.focusView(this.views.intro?.querySelector('h2'));
      } else if (action === 'add-selected') {
        this.addSelected(false);
      } else if (action === 'add-selected-checkout') {
        this.addSelected(true);
      }
    }

    reset() {
      this.answers = {};
      this.stepIndex = 0;
      this.recommendations = [];
      this.isFinishing = false;
      this.setNotice('', 'success');
      if (this.resultsGrid) this.resultsGrid.innerHTML = '';
      if (this.summary) this.summary.innerHTML = '';
      if (this.selectionCopy) this.selectionCopy.textContent = this.copy('selection_default');
      this.setResultsActionsAvailable(true);
    }

    showView(viewName) {
      Object.entries(this.views).forEach(([name, element]) => {
        const isVisible = name === viewName;
        element.hidden = !isVisible;
        element.setAttribute('aria-hidden', String(!isVisible));
      });
    }

    focusView(target) {
      if (!target) return;
      globalScope.requestAnimationFrame(() => {
        if (typeof globalScope.trapFocus === 'function') {
          globalScope.trapFocus(this.modal, target);
        } else {
          target.focus({ preventScroll: true });
        }
      });
    }

    getCurrentQuestion() {
      return this.questions[this.stepIndex] || null;
    }

    renderQuestion() {
      const question = this.getCurrentQuestion();
      if (!question) return;

      this.showView('question');
      this.stepCopy.textContent = this.interpolate(this.copy('step'), {
        CURRENT: this.stepIndex + 1,
        TOTAL: this.questions.length
      });
      const progress = ((this.stepIndex + 1) / this.questions.length) * 100;
      this.progressFill.style.width = `${progress}%`;
      this.progressBar.setAttribute('aria-valuemax', String(this.questions.length));
      this.progressBar.setAttribute('aria-valuenow', String(this.stepIndex + 1));
      this.progressBar.setAttribute('aria-valuetext', this.stepCopy.textContent);
      this.questionTitle.textContent = question.title;
      this.questionCopy.textContent = question.copy;
      this.answersContainer.innerHTML = question.answers.map((answer) => `
        <button type="button" class="kex-business-quiz__answer" data-quiz-answer="${escapeHtml(answer.id)}">
          <span class="kex-business-quiz__answer-title">${escapeHtml(answer.label)}</span>
          <span class="kex-business-quiz__answer-text">${escapeHtml(answer.hint)}</span>
        </button>
      `).join('');
      this.focusView(this.questionTitle);
    }

    handleAnswer(answerId) {
      if (this.isFinishing) return;
      const question = this.getCurrentQuestion();
      const translatedAnswer = question?.answers?.find((answer) => answer.id === answerId);
      if (!question || !translatedAnswer || !resolveAnswerRule(question.id, answerId)) return;

      this.answersContainer.querySelectorAll('button').forEach((button) => {
        button.disabled = true;
      });
      this.answers[question.id] = answerId;

      if (this.stepIndex < this.questions.length - 1) {
        this.stepIndex += 1;
        this.renderQuestion();
      } else {
        this.finishQuiz();
      }
    }

    async finishQuiz() {
      if (this.isFinishing) return;
      this.isFinishing = true;
      this.showLoading(this.copy('loading_catalog'));

      try {
        const catalog = await this.loadCatalog();
        this.recommendations = scoreProducts(catalog, this.answers, this.context, 6);
        this.renderResults();
      } catch (error) {
        this.showCatalogError();
      } finally {
        this.isFinishing = false;
      }
    }

    showLoading(message) {
      this.showView('loading');
      this.loadingMessage.textContent = message;
      this.focusView(this.views.loading?.querySelector('[data-quiz-loading-title]'));
    }

    showCatalogError() {
      this.showView('results');
      this.summary.innerHTML = '';
      this.resultsGrid.innerHTML = `<div class="kex-business-quiz__notice is-error">${escapeHtml(this.copy('catalog_error'))}</div>`;
      this.selectionCopy.textContent = this.copy('catalog_error_selection');
      this.setResultsActionsAvailable(false);
      this.focusView(this.resultsTitle);
    }

    cacheKey() {
      const locale = this.config.locale || 'default';
      const currency = this.config.currency || 'default';
      return `kex-business-quiz-catalog-${CACHE_VERSION}-${locale}-${currency}`;
    }

    readCachedCatalog() {
      try {
        const raw = globalScope.sessionStorage.getItem(this.cacheKey());
        if (!raw) return null;
        const payload = JSON.parse(raw);
        if (!payload || !Array.isArray(payload.products) || !payload.createdAt) return null;
        if (Date.now() - Number(payload.createdAt) > CACHE_TTL) {
          globalScope.sessionStorage.removeItem(this.cacheKey());
          return null;
        }
        const products = payload.products.map(restoreCachedProduct).filter(Boolean);
        return products.length ? products : null;
      } catch (error) {
        return null;
      }
    }

    writeCachedCatalog(products) {
      try {
        globalScope.sessionStorage.setItem(this.cacheKey(), JSON.stringify({
          createdAt: Date.now(),
          products
        }));
      } catch (error) {
        return;
      }
    }

    invalidateCatalog() {
      this.catalog = null;
      try {
        globalScope.sessionStorage.removeItem(this.cacheKey());
      } catch (error) {
        return;
      }
    }

    async loadCatalog(options = {}) {
      if (options.force) this.invalidateCatalog();
      if (this.catalog) return this.catalog;
      if (this.catalogPromise) return this.catalogPromise;

      const cached = this.readCachedCatalog();
      if (cached) {
        this.catalog = cached;
        return this.catalog;
      }

      this.catalogPromise = this.fetchCatalog()
        .then((products) => {
          this.catalog = products;
          this.writeCachedCatalog(products);
          return products;
        })
        .finally(() => {
          this.catalogPromise = null;
        });
      return this.catalogPromise;
    }

    async fetchJson(url) {
      const controller = new AbortController();
      const timeout = globalScope.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
        return response.json();
      } finally {
        globalScope.clearTimeout(timeout);
      }
    }

    async fetchCatalog() {
      const rawProducts = [];
      const productsUrl = new URL(this.config.routes.products, globalScope.location.origin);

      for (let page = 1; page <= MAX_PAGES; page += 1) {
        productsUrl.searchParams.set('limit', String(PAGE_SIZE));
        productsUrl.searchParams.set('page', String(page));
        const payload = await this.fetchJson(productsUrl.toString());
        const pageProducts = Array.isArray(payload?.products) ? payload.products : [];
        rawProducts.push(...pageProducts);
        if (pageProducts.length < PAGE_SIZE) break;
        if (page === MAX_PAGES) throw new Error('Catalog pagination limit reached');
      }

      const deduped = new Map();
      rawProducts.forEach((product) => {
        if (product?.id && !deduped.has(product.id)) deduped.set(product.id, product);
      });
      const products = [...deduped.values()]
        .map((product) => normalizeCatalogProduct(product, this.config.routes))
        .filter(Boolean);
      if (!products.length) throw new Error('Catalog is empty');
      return products;
    }

    answerLabel(questionId, answerId) {
      const question = this.questions.find((item) => item.id === questionId);
      return question?.answers?.find((answer) => answer.id === answerId)?.label || '';
    }

    recommendationReasons(recommendation) {
      const reasons = recommendation.matches
        .map((match) => this.answerLabel(match.questionId, match.answerId))
        .filter(Boolean)
        .slice(0, 2)
        .map((label) => this.interpolate(this.copy('match_reason'), { VALUE: label }));
      return reasons.length ? reasons : [this.copy('profile_match')];
    }

    renderResults() {
      this.showView('results');
      this.setNotice('', 'success');
      this.setResultsActionsAvailable(true);

      const selectedAnswerLabels = this.questions.map((question) => (
        this.answerLabel(question.id, this.answers[question.id])
      )).filter(Boolean);
      this.summary.innerHTML = selectedAnswerLabels.map((label) => `
        <span class="kex-business-quiz__summary-pill">${escapeHtml(label)}</span>
      `).join('');

      if (!this.recommendations.length) {
        this.resultsGrid.innerHTML = `<div class="kex-business-quiz__notice is-error">${escapeHtml(this.copy('no_matches'))}</div>`;
        this.selectionCopy.textContent = this.copy('no_matches_selection');
        this.setResultsActionsAvailable(false);
        this.focusView(this.resultsTitle);
        return;
      }

      this.resultsGrid.innerHTML = this.recommendations.map((recommendation, index) => {
        const { product } = recommendation;
        const reasons = this.recommendationReasons(recommendation);
        const inputId = `quiz-product-${product.id}`;
        const isChecked = index < 3;
        return `
          <article class="kex-business-quiz__product${isChecked ? ' is-selected' : ''}" data-quiz-card>
            <div class="kex-business-quiz__product-select">
              <label class="kex-business-quiz__product-toggle" for="${inputId}">
                <input type="checkbox" id="${inputId}" data-quiz-select value="${product.variant_id}" ${isChecked ? 'checked' : ''}>
                <span>${escapeHtml(this.copy('include_product'))}</span>
              </label>
              <a href="${escapeHtml(product.url)}" class="link text-sm">${escapeHtml(this.copy('view_product'))}</a>
            </div>
            <div class="kex-business-quiz__product-media">
              ${product.image
                ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="480" height="480">`
                : '<div class="kex-business-quiz__product-placeholder">KEX</div>'}
            </div>
            <div class="kex-business-quiz__product-body">
              <span class="kex-business-quiz__product-eyebrow">${escapeHtml(product.type || product.vendor || this.copy('recommended'))}</span>
              <h4 class="kex-business-quiz__product-title">${escapeHtml(product.title)}</h4>
              ${product.subtitle ? `<p class="kex-business-quiz__product-subtitle">${escapeHtml(product.subtitle)}</p>` : ''}
              <div class="kex-business-quiz__reasons">
                ${reasons.map((reason) => `<span class="kex-business-quiz__reason">${escapeHtml(reason)}</span>`).join('')}
              </div>
              <div class="kex-business-quiz__product-footer">
                <strong class="kex-business-quiz__product-price">${escapeHtml(this.formatMoney(product.price))}</strong>
                <button type="button" class="kex-business-quiz__single-add" data-quiz-single-add="${product.variant_id}">
                  ${escapeHtml(this.copy('add_single'))}
                </button>
              </div>
            </div>
          </article>
        `;
      }).join('');

      this.syncSelectedCards();
      this.updateSelectionCopy();
      this.focusView(this.resultsTitle);
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        const options = this.config.currency
          ? { style: 'currency', currency: this.config.currency }
          : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
        return new Intl.NumberFormat(this.config.locale || undefined, options).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${this.config.currency || ''}`.trim();
      }
    }

    syncSelectedCards() {
      this.root.querySelectorAll('[data-quiz-card]').forEach((card) => {
        const checkbox = card.querySelector('[data-quiz-select]');
        card.classList.toggle('is-selected', Boolean(checkbox?.checked));
      });
    }

    updateSelectionCopy() {
      const count = this.getSelectedItems().length;
      if (count === 0) {
        this.selectionCopy.textContent = this.copy('selection_zero');
      } else if (count === 1) {
        this.selectionCopy.textContent = this.copy('selection_one');
      } else {
        this.selectionCopy.textContent = this.interpolate(this.copy('selection_many'), { COUNT: count });
      }
    }

    getSelectedItems() {
      return [...this.root.querySelectorAll('[data-quiz-select]:checked')]
        .map((input) => ({ id: Number(input.value), quantity: 1 }))
        .filter((item) => item.id > 0);
    }

    async addSelected(checkout) {
      const items = this.getSelectedItems();
      if (!items.length) {
        this.setNotice(this.copy('select_before_continue'), 'error');
        this.focusView(this.notice);
        return;
      }
      await this.addItems(items, { checkout });
    }

    async addItems(items, options = {}) {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      this.toggleActionButtons(true, options.checkout ? this.copy('preparing_checkout') : this.copy('adding'));

      try {
        const route = String(this.config.routes.cartAdd || '/cart/add');
        const response = await fetch(route.endsWith('.js') ? route : `${route}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items })
        });
        const payload = await response.json();
        if (!response.ok || payload.status) {
          this.invalidateCatalog();
          throw new Error(payload.description || payload.message || this.copy('cart_error'));
        }

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        if (options.checkout) {
          this.modal.close();
          globalScope.location.href = this.config.routes.checkout;
          return;
        }

        this.modal.close();
        if (globalScope.theme?.settings?.cartType === 'drawer') {
          globalScope.setTimeout(() => {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
          }, 140);
        } else {
          globalScope.location.href = this.config.routes.cart;
        }
      } catch (error) {
        this.setNotice(error.message || this.copy('cart_error'), 'error');
        this.focusView(this.notice);
      } finally {
        this.isSubmitting = false;
        this.toggleActionButtons(false);
      }
    }

    toggleActionButtons(isBusy, overrideLabel = '') {
      this.root.querySelectorAll('[data-quiz-action="add-selected"], [data-quiz-action="add-selected-checkout"], [data-quiz-single-add]').forEach((button) => {
        if (!button.dataset.defaultLabel) button.dataset.defaultLabel = button.textContent.trim();
        button.disabled = isBusy;
        button.textContent = isBusy && overrideLabel ? overrideLabel : button.dataset.defaultLabel;
      });
    }

    setResultsActionsAvailable(available) {
      this.root.querySelectorAll('[data-quiz-action="add-selected"], [data-quiz-action="add-selected-checkout"]').forEach((button) => {
        button.hidden = !available;
      });
    }

    setNotice(message, tone) {
      if (!message) {
        this.notice.hidden = true;
        this.notice.textContent = '';
        this.notice.classList.remove('is-error');
        this.notice.setAttribute('role', 'status');
        this.notice.setAttribute('aria-live', 'polite');
        return;
      }
      this.notice.hidden = false;
      this.notice.textContent = message;
      this.notice.classList.toggle('is-error', tone === 'error');
      this.notice.setAttribute('role', tone === 'error' ? 'alert' : 'status');
      this.notice.setAttribute('aria-live', tone === 'error' ? 'assertive' : 'polite');
    }
  }

  const modal = document.getElementById(QUIZ_MODAL_ID);
  if (modal) new KexBusinessQuiz(modal);
})(typeof window !== 'undefined' ? window : globalThis);
